import React, { useState, useEffect } from "react";
import {
  Loader,
  Table,
  Dropdown,
  Icon,
  Button,
  Label,
  Popup,
  Message,
} from "semantic-ui-react";
import { AirEmissionsform } from "../../../../api";
import { useAuth } from "../../../../hooks";
import {
  hasPermission,
  isAdmin,
  isMaster,
  isEditor,
} from "../../../../utils/checkPermission";
import "./ListAirEmissionsForms.scss";
import { BasicDrawer, ConfirmModal } from "../../../Shared";
import { PERIODS } from "../../../../utils";
import { formatDateView } from "../../../../utils/formatDate";
import { AirEmissionsForm } from "../AirEmissionsForm";
import { useLanguage } from "../../../../contexts";

import {
  determineAirEmissionsUnits,
  airemissionsCodes,
} from "../../../../utils/dataAndFormulas/airemissionsForms";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UnitsForm } from "../UnitsForm/UnitsForm";
import { UpdateUnitsModal } from "../UpdateUnitsModal";
import { Form } from "../../../../api";

const _ = require("lodash");

const airEmissionsFormController = new AirEmissionsform();
const formController = new Form();

export function ListAirEmissionsForms(props) {
  const { reload, onReload, siteSelected, yearSelected, configFormData } =
    props;
  const [airEmissionsForms, setAirEmissionsForms] = useState([]);
  const [loading, setLoading] = useState(false);

  const { user, accessToken } = useAuth();
  const { role, site } = user;

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key;

  useEffect(() => {
    (async () => {
      if (yearSelected !== "" && siteSelected !== undefined) {
        setLoading(true);
        try {
          const response =
            await airEmissionsFormController.getAirEmissionsFormsBySiteAndYear(
              accessToken,
              siteSelected,
              yearSelected
            );

          if (response.code === 200) {
            setAirEmissionsForms(response.airEmissionsForms);
          } else {
            setAirEmissionsForms([]);
          }
        } catch (error) {
          // console.error(error);
          setAirEmissionsForms([]);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [yearSelected, siteSelected, reload]);

  if (loading)
    return (
      <Loader
        active
        inline="centered"
        content="Cargando datos de emisiones..."
      />
    );

  return (
    <div className="list-airemissions-forms">
      <TablePeriods
        data={airEmissionsForms}
        reload={reload}
        onReload={onReload}
        accessToken={accessToken}
        year={yearSelected}
        site={siteSelected}
        t={t}
        configFormData={configFormData}
        language={language}
        user={user}
      />
    </div>
  );
}

function TablePeriods(props) {
  const {
    data,
    reload,
    onReload,
    accessToken,
    year,
    site,
    t,
    configFormData,
    language,
    user,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [dataDeleted, setDataDelete] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [unitsRecorded, setUnitsRecorded] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [expandedFields, setExpandedFields] = useState({});

  // Para mostrar mensaje de alerta
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Estado para controlar el período seleccionado
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Estados para edición múltiple
  const [editMode, setEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para el modal de unidades
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showMissingUnitsWarning, setShowMissingUnitsWarning] = useState(false);
  const [showUnitsWarning, setShowUnitsWarning] = useState(false);

  // Estado para el modal de modificar unidades en formularios existentes
  const [showUpdateUnitsModal, setShowUpdateUnitsModal] = useState(false);
  const [isUpdatingUnits, setIsUpdatingUnits] = useState(false);

  // Estado para el modal de advertencia de unidades
  const [showWarningModal, setShowWarningModal] = useState(false);

  // Estado para el modal de advertencia de cambio de unidades (l → L)
  const [showLegacyUnitWarning, setShowLegacyUnitWarning] = useState(false);
  const [pendingUnitsToSave, setPendingUnitsToSave] = useState(null);

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);
  const onOpenCloseUnitsModal = () =>
    setShowUnitsModal((prevState) => !prevState);
  const onOpenCloseUpdateUnitsModal = () =>
    setShowUpdateUnitsModal((prevState) => !prevState);

  useEffect(() => {
    (async () => {
      try {
        if (site && year) {
          const fields = determineFields();
          setUniqueFields(fields);
        }
      } catch (error) {
        setUniqueFields([]);
      }
    })();
  }, [configFormData, year, site, reload]);

  useEffect(() => {
    if (!uniqueFields?.length) return;

    const fetchUnits = async () => {
      try {
        let recordedUnits = {};
        let unitsFromConfig = false;

        // 1. Primero intentar obtener las unidades desde configFormData.fields (Form)
        if (configFormData?.fields && configFormData.fields.length > 0) {
          configFormData.fields.forEach((field) => {
            if (field.unit && field.name) {
              recordedUnits[field.name] = field.unit;
            }
          });
          if (Object.keys(recordedUnits).length > 0) {
            unitsFromConfig = true;
          }
          // console.log(
          //   "✅ Unidades cargadas desde configFormData:",
          //   recordedUnits
          // );
        }

        setUnitsRecorded(recordedUnits);

        // 2. Construir selectedUnits basándose solo en lo registrado (sin defaults)
        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = recordedUnits[field] || "";
        });

        // console.log("✅ selectedUnits actualizado:", values);
        setSelectedUnits(values);

        // Mostrar advertencia solo si no hay configFormData configurado y hay unidades de formularios
        setShowUnitsWarning(
          !configFormData && Object.keys(recordedUnits).length > 0
        );

        // 3. Validar campos activos sin unidades en configFormData
        if (configFormData?.fields && configFormData.fields.length > 0) {
          const activeFieldsWithoutUnit = configFormData.fields.filter(
            (field) => field.active && !field.unit
          );

          if (activeFieldsWithoutUnit.length > 0) {
            setShowMissingUnitsWarning(true);
          } else {
            setShowMissingUnitsWarning(false);
          }
        } else {
          setShowMissingUnitsWarning(false);
        }
      } catch (error) {
        // console.error("❌ Error al cargar unidades:", error);
        setUnitsRecorded({});
        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = "";
        });
        setSelectedUnits(values);
      }
    };

    fetchUnits();
  }, [uniqueFields, configFormData, reload]);

  // Alerta antes de salir de la página si hay cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges && editMode) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges, editMode]);

  // Atajos de teclado para guardar (Ctrl+S) y cancelar (Esc)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editMode) {
        if (e.ctrlKey && e.key === "s") {
          e.preventDefault();
          handleSaveChanges();
        } else if (e.key === "Escape") {
          handleCancelEdit();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editMode, editedValues]);

  // Función para abrir el drawer con datos del período seleccionado
  const handleOpenModal = (period) => {
    setSelectedPeriod(data.find((item) => item.period === period));
    onOpenCloseModal();
  };

  // Función para confirmar eliminación
  const handleDeleteModal = (period) => {
    const form = data.find((item) => item.period === period);
    setConfirmContent(
      `${t("delete_dated_airemissions_form")} ${formatDateView(form.date)}`
    );
    setDataDelete(form);
    onOpenCloseConfirm();
  };

  // Función para verificar si hay al menos una unidad configurada
  const validateUnitsConfigured = () => {
    if (!configFormData?.fields || configFormData.fields.length === 0) {
      return { valid: true };
    }

    const activeFields = configFormData.fields.filter(
      (field) => field.active !== false
    );

    if (activeFields.length === 0) {
      return { valid: false, message: t("no_active_fields") };
    }

    const fieldsWithUnit = activeFields.filter(
      (field) => field.unit && field.unit !== ""
    );

    if (fieldsWithUnit.length === 0) {
      return {
        valid: false,
        message:
          t("units_not_configured") ||
          "Debe configurar al menos una unidad antes de agregar un periodo. Haga clic en el ícono de tres puntos (⋯) en la columna de Unidades.",
      };
    }

    return { valid: true };
  };

  const handleUnitsFormSave = (units) => {
    setPendingUnitsToSave(units);
    setShowLegacyUnitWarning(true);
  };

  // Función para guardar unidades en el Form
  const handleSaveUnits = async (units) => {
    try {
      // console.log("💾 Iniciando handleSaveUnits (AirEmissions)...");
      // console.log("📋 configFormData:", configFormData);

      const formId = configFormData?._id;
      const currentFields = configFormData?.fields || [];

      // console.log(
      //   `🔍 formId: ${formId}, currentFields.length: ${currentFields.length}`
      // );

      // Todos los campos de air emissions
      const allAirEmissionsFieldsData = [
        { name: "carbon_dioxide", code: "Air1" },
        { name: "carbon_monoxide", code: "Air2" },
        { name: "particulate_material", code: "Air3" },
        { name: "volatile_organic_compounds", code: "Air4" },
        { name: "sulfur_oxides", code: "Air5" },
        { name: "nitrogen_oxides", code: "Air6" },
        { name: "chlorofluorocarbons", code: "Air7" },
        { name: "hydrochlorofluorocarbons", code: "Air8" },
        { name: "hydrofluorocarbons", code: "Air9" },
        { name: "hydrofluoroolefins", code: "Air10" },
        { name: "perfluorocarbons", code: "Air11" },
        { name: "substances_exhaust_ozone_layer", code: "Air12" },
        { name: "ozone", code: "Air13" },
        { name: "toxic_or_dangerous_atmospheric_pollutants", code: "Air14" },
        { name: "ammonia", code: "Air15" },
        { name: "propane", code: "Air16" },
        { name: "isobutane", code: "Air17" },
        { name: "propylene", code: "Air18" },
        { name: "ethylene", code: "Air19" },
        { name: "hexafluoroethane", code: "Air20" },
        { name: "octafluoropropane", code: "Air21" },
        { name: "chlorotrifluoromethane", code: "Air22" },
        { name: "chlorodifluoromethane", code: "Air23" },
        { name: "tetrafluoroethane", code: "Air24" },
        { name: "trifluoromethane", code: "Air25" },
        { name: "pentafluoroethane", code: "Air26" },
        { name: "hexafluoropropylene", code: "Air27" },
        { name: "hydrogen", code: "Air28" },
        { name: "methane", code: "Air29" },
        { name: "oxygen", code: "Air30" },
        { name: "nitrogen", code: "Air31" },
        { name: "hydrogen_sulfide", code: "Air32" },
        { name: "carbon_disulfide", code: "Air33" },
        { name: "nitrous_oxide", code: "Air34" },
        { name: "hydrochlorofluorocarbons_ref", code: "Ref1" },
        { name: "hydrofluorocarbons_ref", code: "Ref2" },
        { name: "hydrofluoroolefins_ref", code: "Ref3" },
        { name: "perfluorocarbonos_ref", code: "Ref4" },
      ];

      if (!formId) {
        // console.log(
        //   "⚠️ No hay formId en configFormData, verificando en la base de datos..."
        // );

        const existingForms = await formController.getFormsBySite(
          accessToken,
          site,
          "airEmissions"
        );

        // console.log(`🔍 Forms existentes en BD: ${existingForms.length}`);

        if (existingForms.length > 0) {
          // console.error(
          //   "❌ ERROR: Ya existe un Form en la BD pero configFormData no lo tiene!"
          // );
          // console.log("🔄 Usando el Form existente:", existingForms[0]._id);

          const existingFormId = existingForms[0]._id;
          const existingFields = existingForms[0].fields || [];

          const changes = existingFields
            .filter((field) => field.name in units)
            .map((field) => ({ name: field.name, unit: units[field.name] }));

          const response =
            changes.length > 0
              ? await formController.updateFormFields(accessToken, existingFormId, changes)
              : { code: 200 };

          if (response.code === 200) {
            toast.success(t("units_updated_successfully"));
            setShowUnitsWarning(false);
            onReload();
          } else {
            toast.error(t("error_updating_units"));
          }
          return;
        }

        // Si NO existe ningún Form, crear uno nuevo
        // console.log("✅ No hay Form en la BD, creando uno nuevo...");
        const fieldsToCreate = allAirEmissionsFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          standardized_coefficient: 1,
        }));

        const newFormData = {
          module: "airEmissions",
          site: site,
          fields: fieldsToCreate,
        };

        // console.log("📝 Creando nuevo Form con data:", newFormData);

        const response = await formController.createForm(
          accessToken,
          newFormData
        );
        if (response.code === 200 || response._id) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false);
          onReload();
        } else {
          toast.error(t("error_updating_units"));
        }
      } else if (currentFields.length === 0) {
        // Existe Form pero sin campos, actualizar con todos los campos
        // console.log(
        //   "🔄 AirEmissions - Actualizando Form existente SIN campos previos"
        // );

        const fieldsToCreate = allAirEmissionsFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          standardized_coefficient: 1,
        }));

        const response = await formController.updateFormFields(accessToken, formId, fieldsToCreate);

        if (response.code === 200) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false);
          onReload();
        } else {
          toast.error(t("error_updating_units"));
        }
      } else {
        // Ya existe un Form con campos: solo se envían los que cambian de
        // unidad, de forma atómica (ver updateFormFields en el
        // controlador) — no se pisa el documento entero.
        const changes = currentFields
          .filter((field) => field.name in units)
          .map((field) => ({ name: field.name, unit: units[field.name] }));

        const response =
          changes.length > 0
            ? await formController.updateFormFields(accessToken, formId, changes)
            : { code: 200 };

        // console.log("📝 Respuesta de actualización de Form:", response);

        if (response.code === 200) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false);
          // console.log("✅ Llamando onReload() para actualizar configFormData...");
          onReload();
        } else {
          // console.error("❌ Error en respuesta:", response);
          toast.error(t("error_updating_units"));
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_updating_units"));
    }
  };

  const openUpdateAirEmissionsForm = (period) => {
    const form = data.find((item) => item.period === period);
    setModalContent(
      <AirEmissionsForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        airEmissionsForm={form}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />
    );
    setShowModal(true);
  };

  const openNewAirEmissionsForm = (period) => {
    // Validar que las unidades estén configuradas antes de abrir el formulario
    const validation = validateUnitsConfigured();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setModalContent(
      <AirEmissionsForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        period={period}
        year={year}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
      />
    );
    setShowModal(true);
  };

  // Función para aplicar unidades a formularios existentes (modal separado)
  const handleApplyUnitsToForms = async (unitsToApply) => {
    if (!unitsToApply || Object.keys(unitsToApply).length === 0) {
      toast.warning(t("no_fields_selected") || "No hay campos seleccionados");
      return;
    }

    setIsUpdatingUnits(true);
    try {
      const response =
        await airEmissionsFormController.updateUnitsForYear(
          accessToken,
          site,
          year,
          unitsToApply
        );

      if (response.code === 200) {
        toast.success(
          `${
            t("units_applied_to_forms") || "Unidades aplicadas a"
          } ${response.updated} ${t("forms") || "formularios"}`
        );
        onOpenCloseUpdateUnitsModal();
        onReload();
      } else {
        toast.error(
          t("error_applying_units_to_forms") ||
            "Error al aplicar unidades"
        );
      }
    } catch (error) {
      // console.error("Error applying units:", error);
      toast.error(
        t("error_applying_units_to_forms") || "Error al aplicar unidades"
      );
    } finally {
      setIsUpdatingUnits(false);
    }
  };

  const onDelete = async () => {
    try {
      const response =
        await airEmissionsFormController.deleteAirEmissionsForm(
          accessToken,
          dataDeleted._id
        );
      if (response.code === 200) {
        setIsSuccess(true);
        setModalMessage("Formulario eliminado correctamente!");
      } else {
        setIsSuccess(false);
        setModalMessage("Error al eliminar el formulario.");
      }

      setModalOpen(true);
      setDataDelete("");
      onOpenCloseConfirm();
      onReload();
    } catch (error) {
      console.error(error);
      setIsSuccess(false);
      setModalMessage("Error al eliminar el formulario.");
      setModalOpen(true);
    }
  };

  // Obtén los nombres de los períodos para las columnas
  const periods = PERIODS;

  // Determinar los campos únicos según configuración activa
  const determineFields = () => {
    const fields = new Set();

    if (
      configFormData &&
      configFormData.fields &&
      configFormData.fields.length > 0
    ) {
      Object.keys(airemissionsCodes).forEach((key) => {
        if (
          configFormData.fields &&
          configFormData.fields.some(
            (field) => field.name === key && field.active
          )
        ) {
          fields.add(key);
        }
      });
    } else {
      Object.keys(airemissionsCodes).forEach((key) => {
        fields.add(key);
      });
    }

    return Array.from(fields);
  };

  const hasDataPeriod = (period) => {
    const existsPeriod = data.some((entry) => entry.period === period);
    return existsPeriod;
  };

  // Función para formatear números sin decimales y con puntos en miles
  function formatNumberWithThousands(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }
    const entero = Math.round(numero);
    return entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  const calculateTotalAndAverage = (field) => {
    const values = data.map((item) => item[field]?.value || 0);

    const totalFinal = values.reduce((ac, value) => ac + value, 0);
    const total = formatNumberWithThousands(totalFinal);
    const averageFinal = values.length ? totalFinal / values.length : 0;
    const average = formatNumberWithThousands(averageFinal);
    return { total, average };
  };

  const getUnitName = (field) => {
    const unitValue = selectedUnits[field];

    if (!unitValue || unitValue === "") {
      return "- - -";
    }

    const unitsArray = determineAirEmissionsUnits(field);
    const unitObject = unitsArray.find((u) => u.value === unitValue);
    return unitObject?.name || unitValue;
  };

  const handleUnitChange = (rowIndex, newValue) => {
    setSelectedUnits({ ...selectedUnits, [rowIndex]: newValue.value });
  };

  const getFieldByGroup = (field, type) => {
    const value = airemissionsCodes[field];
    return value.startsWith(type);
  };

  // ========== FUNCIONES PARA EDICIÓN MÚLTIPLE ==========

  const handleCellChange = (field, period, item, newValue) => {
    const key = `${period}_${field}`;
    setEditedValues((prev) => ({
      ...prev,
      [key]: {
        formId: item?._id || null,
        period: period,
        field,
        value: newValue,
        unit: item?.[field]?.unit || selectedUnits[field],
        isNew: !item || !item._id,
        year: year,
        site: site,
      },
    }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges || Object.keys(editedValues).length === 0) {
      toast.info("No hay cambios para guardar");
      return;
    }

    setIsSaving(true);

    try {
      const updates = {};
      const creates = [];

      Object.entries(editedValues).forEach(([key, change]) => {
        if (change.isNew) {
          creates.push({
            period: change.period,
            year: change.year,
            site: change.site,
            field: change.field,
            value: change.value,
            unit: change.unit,
          });
        } else {
          updates[key] = {
            formId: change.formId,
            field: change.field,
            value: change.value,
            unit: change.unit,
          };
        }
      });

      const response =
        await airEmissionsFormController.batchUpdateAirEmissionsForms(
          accessToken,
          {
            updates,
            creates,
            site,
            year,
            creatorUser: user._id,
          }
        );

      if (response.code === 200) {
        toast.success(
          `✅ ${response.updated || 0} formularios actualizados, ${
            response.created || 0
          } creados`
        );
        setEditedValues({});
        setHasChanges(false);
        setEditMode(false);
        onReload();
      } else {
        toast.error(response.msg || "Error al guardar los cambios");
      }
    } catch (error) {
      // console.error("Error saving changes:", error);
      toast.error("Error al guardar los cambios");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        "Hay cambios sin guardar. ¿Deseas cancelar la edición?"
      );
      if (!confirmed) return;
    }
    setEditMode(false);
    setEditedValues({});
    setHasChanges(false);
  };

  const toggleEditMode = () => {
    if (editMode && hasChanges) {
      const confirmed = window.confirm(
        "Hay cambios sin guardar. ¿Deseas salir del modo edición?"
      );
      if (!confirmed) return;
    }
    setEditMode(!editMode);
    setEditedValues({});
    setHasChanges(false);
  };

  const toggleExpand = (field) => {
    setExpandedFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <>
      {/* Botones de control de edición múltiple */}
      {(isMaster(user.role) || isAdmin(user.role) || isEditor(user.role)) && (
      <div className="batch-actions">
        {!editMode ? (
          <Button
            className="batch-btn primary"
            icon
            onClick={toggleEditMode}
            disabled={data.length === 0}
          >
            <Icon name="edit" />
          </Button>
        ) : (
          <>
            <Button
              className="batch-btn success"
              icon
              labelPosition="left"
              onClick={handleSaveChanges}
              disabled={!hasChanges || isSaving}
              loading={isSaving}
            >
              <Icon name="save" />
              Guardar Cambios
              {hasChanges && ` (${Object.keys(editedValues).length})`}
            </Button>
            <Button
              className="batch-btn cancel"
              icon
              labelPosition="left"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              <Icon name="cancel" />
              Cancelar
            </Button>
            {hasChanges && (
              <span style={{ color: "#f57c00", fontWeight: "bold" }}>
                <Icon name="warning sign" />
                {Object.keys(editedValues).length} cambio(s) pendiente(s)
              </span>
            )}
          </>
        )}
      </div>
      )}

      {/* Mensaje de cambios pendientes */}
      {hasChanges && (
        <Message warning>
          <Message.Header>Cambios sin guardar</Message.Header>
          <p>
            Tienes {Object.keys(editedValues).length} cambio(s) pendiente(s). No
            olvides guardar antes de salir. (Ctrl+S para guardar, Esc para
            cancelar)
          </p>
        </Message>
      )}

      {/* Advertencia de unidades tomadas de formularios */}
      {showUnitsWarning && (
        <Message info>
          <Message.Header>Configuración de unidades recomendada</Message.Header>
          <p>
            Las unidades mostradas provienen de formularios guardados. Para una
            mejor consistencia, se recomienda configurar las unidades
            explícitamente haciendo clic en el ícono de tres puntos (⋯) en la
            columna "Unidades".
          </p>
        </Message>
      )}

      <div className="list-airemissions-forms__table-wrapper">
        <Table className="airemissions-table" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="sticky-col-1 pill-left-start">
                {t("code")}
              </Table.HeaderCell>
              <Table.HeaderCell className="sticky-col-2">
                {t("concept")}
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-left-end">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  {t("units")}
                  {showMissingUnitsWarning && (
                    <Icon
                      name="warning sign"
                      color="orange"
                      style={{ cursor: "pointer", margin: 0 }}
                      onClick={() => setShowWarningModal(true)}
                    />
                  )}
                  <Dropdown
                    icon="ellipsis vertical"
                    floating
                    className="icon"
                    style={{ cursor: "pointer" }}
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        text={t("configure_units") || "Configurar unidades"}
                        icon="setting"
                        onClick={onOpenCloseUnitsModal}
                      />
                      {data.length > 0 && (
                        <Dropdown.Item
                          text={
                            t("update_units_in_forms") ||
                            "Modificar unidades en formularios"
                          }
                          icon="edit"
                          onClick={onOpenCloseUpdateUnitsModal}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-gap" />
              <Table.HeaderCell
                colSpan="3"
                textAlign="center"
                className="pill-right"
              >
                {t("report_period")} {year}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {uniqueFields.map((field, index) => {
              const isExpanded = expandedFields[field];
              const { total, average } = calculateTotalAndAverage(field);
              const showAirHeader = index === 0 && getFieldByGroup(field, "Air");
              const showRefHeader =
                getFieldByGroup(field, "Ref") &&
                (index === 0 || getFieldByGroup(uniqueFields[index - 1], "Air"));

              return (
                <React.Fragment key={field}>
                  {showAirHeader && (
                    <Table.Row className="airemissions-section-header">
                      <Table.Cell colSpan="7">Procesos y Operaciones</Table.Cell>
                    </Table.Row>
                  )}
                  {showRefHeader && (
                    <Table.Row className="airemissions-section-header">
                      <Table.Cell colSpan="7">Refrigerantes</Table.Cell>
                    </Table.Row>
                  )}
                    {/* Fila resumen */}
                    <Table.Row>
                      <Table.Cell className="sticky-col-1 pill-left-start">
                        {airemissionsCodes[field]}
                      </Table.Cell>
                      <Table.Cell className="sticky-col-2">
                        {t(field)}
                      </Table.Cell>
                      <Table.Cell
                        textAlign="center"
                        className="pill-left-end"
                      >
                        {getUnitName(field)}
                      </Table.Cell>
                      <Table.Cell className="pill-gap" />
                      <Table.Cell colSpan="3" className="pill-right">
                        <div className="summary-flex">
                          <span className="summary-item">
                            {t("total")}: <strong>{total}</strong>
                          </span>
                          <span className="summary-item">
                            {t("average")}: <strong>{average}</strong>
                          </span>
                          <Button
                            icon
                            size="tiny"
                            className="expand-btn"
                            onClick={() => toggleExpand(field)}
                          >
                            <Icon
                              name={
                                isExpanded ? "chevron up" : "chevron down"
                              }
                            />
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>

                    {/* Fila cabecera de meses (expandida) */}
                    {isExpanded && (
                      <Table.Row className="months-header-row">
                        <Table.Cell
                          colSpan="7"
                          style={{ padding: 0 }}
                          className="months-full-row"
                        >
                          <div className="months-flex-container">
                            {periods.map((period) => (
                              <div
                                key={period}
                                className="month-flex-cell month-flex-header"
                              >
                                {t(period).slice(0, 3)}
                                {!editMode &&
                                  (hasDataPeriod(period) ? (
                                    <Dropdown
                                      inline
                                      size="tiny"
                                      icon="ellipsis vertical"
                                      floating
                                      className="icon"
                                    >
                                      <Dropdown.Menu>
                                        <Dropdown.Item
                                          text={t("edit")}
                                          icon={
                                            <Icon
                                              name="edit"
                                              style={{ color: "gray" }}
                                            />
                                          }
                                          onClick={() =>
                                            openUpdateAirEmissionsForm(period)
                                          }
                                        />
                                        <Dropdown.Item
                                          text={t("delete")}
                                          icon={
                                            <Icon
                                              name="trash"
                                              style={{ color: "red" }}
                                            />
                                          }
                                          onClick={() =>
                                            handleDeleteModal(period)
                                          }
                                        />
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  ) : (
                                    <Dropdown
                                      link
                                      size="tiny"
                                      icon="ellipsis vertical"
                                      floating
                                      className="icon"
                                    >
                                      <Dropdown.Menu>
                                        {(isAdmin(user.role) ||
                                          isEditor(user.role) ||
                                          isMaster(user.role)) && (
                                          <Dropdown.Item
                                            text={t("load_data")}
                                            icon="plus"
                                            onClick={() =>
                                              openNewAirEmissionsForm(period)
                                            }
                                          />
                                        )}
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  ))}
                              </div>
                            ))}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}

                    {/* Fila datos de meses (expandida) */}
                    {isExpanded && (
                      <Table.Row className="months-data-row">
                        <Table.Cell
                          colSpan="7"
                          style={{ padding: 0 }}
                          className="months-full-row"
                        >
                          <div className="months-flex-container">
                            {periods.map((period) => {
                              const item = data.find(
                                (d) => d.period === period
                              );
                              const editKey = `${period}_${field}`;
                              const isEdited =
                                editedValues[editKey] !== undefined;
                              const hasUnit =
                                selectedUnits[field] &&
                                selectedUnits[field] !== "";
                              const itemUnit = item?.[field]?.unit;
                              const configuredUnit = selectedUnits[field];
                              const isDifferentUnit =
                                itemUnit &&
                                configuredUnit &&
                                itemUnit !== configuredUnit;
                              let cellContent;

                              if (editMode) {
                                const displayVal = isEdited
                                  ? editedValues[editKey].value
                                  : item?.[field]?.value ?? "";
                                cellContent = (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={displayVal}
                                    onChange={(e) =>
                                      handleCellChange(
                                        field,
                                        period,
                                        item,
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                    disabled={!hasUnit}
                                    style={{
                                      width: "100%",
                                      height: "30px",
                                      border: isEdited
                                        ? "2px solid #fbc02d"
                                        : "1px solid #ccc",
                                      borderRadius: "4px",
                                      padding: "2px 6px",
                                      textAlign: "right",
                                      fontSize: "11px",
                                      background: hasUnit ? "white" : "#f5f5f5",
                                      color: hasUnit ? "inherit" : "#999",
                                    }}
                                    placeholder="-"
                                  />
                                );
                              } else {
                                cellContent = item?.[field] ? (
                                  <span>
                                    {formatNumberWithThousands(
                                      item[field].value
                                    )}
                                    {isDifferentUnit && (
                                      <span
                                        style={{
                                          fontSize: "0.75em",
                                          color: "#f57c00",
                                          marginLeft: "2px",
                                        }}
                                        title={`Unidad: ${itemUnit} | Configurada: ${configuredUnit}`}
                                      >
                                        ({itemUnit})
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  "-"
                                );
                              }

                              return (
                                <div
                                  key={period}
                                  className="month-flex-cell month-flex-data"
                                >
                                  {cellContent}
                                </div>
                              );
                            })}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}

          </Table.Body>
        </Table>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={1200}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <BasicDrawer show={showModal} close={onOpenCloseModal}>
        {modalContent}
      </BasicDrawer>

      <ConfirmModal
        open={showConfirm}
        title={t("delete")}
        content={confirmContent}
        onConfirm={onDelete}
        onCancel={onOpenCloseConfirm}
      />

      <ConfirmModal
        open={modalOpen}
        icon={<Icon name={isSuccess ? "check circle outline" : "warning sign"} color={isSuccess ? "green" : "red"} />}
        title={isSuccess ? "Éxito" : "Error"}
        content={modalMessage}
        onCancel={() => setModalOpen(false)}
        cancelText="Cerrar"
      />

      <UnitsForm
        show={showUnitsModal}
        onClose={onOpenCloseUnitsModal}
        onSave={handleUnitsFormSave}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
      />

      <UpdateUnitsModal
        show={showUpdateUnitsModal}
        onClose={onOpenCloseUpdateUnitsModal}
        onApply={handleApplyUnitsToForms}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
        isLoading={isUpdatingUnits}
      />

      <ConfirmModal
        open={showWarningModal}
        icon={<Icon name="warning sign" color="orange" />}
        title="Unidades sin configurar"
        content={
          <>
            <p>Los campos no tienen unidades asignadas en la configuración.</p>
            <p>
              Por favor configure las unidades haciendo clic en el ícono{" "}
              <strong>⋯</strong> en la columna Unidades.
            </p>
          </>
        }
        onCancel={() => setShowWarningModal(false)}
        onConfirm={() => {
          setShowWarningModal(false);
          onOpenCloseUnitsModal();
        }}
        cancelText="Cerrar"
        confirmText="Configurar unidades"
      />

      <ConfirmModal
        open={showLegacyUnitWarning}
        icon={<Icon name="warning sign" color="orange" />}
        title="Cambio de unidades"
        content={
          <>
            <p>
              Los registros anteriores continuarán guardados con la unidad{" "}
              <strong>"l"</strong> (litros). A partir de ahora, los nuevos
              datos se guardarán con <strong>"L"</strong> (Litros).
            </p>
            <p>¿Desea continuar con el cambio?</p>
          </>
        }
        onCancel={() => setShowLegacyUnitWarning(false)}
        onConfirm={() => {
          setShowLegacyUnitWarning(false);
          handleSaveUnits(pendingUnitsToSave);
        }}
        cancelText="Cancelar"
        confirmText="Confirmar"
      />
    </>
  );
}

// EditableCell Component
function EditableCell({
  field,
  period,
  item,
  editMode,
  selectedUnits,
  editedValues,
  onChange,
  formatNumberWithThousands,
}) {
  const key = `${period}_${field}`;
  const isEdited = editedValues[key] !== undefined;

  const displayValue = isEdited
    ? editedValues[key].value
    : item?.[field]?.value || "";

  const [localValue, setLocalValue] = useState(displayValue);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    setLocalValue(displayValue);
    setHasInteracted(false);
  }, [displayValue]);

  const handleFocus = () => {
    if (!hasInteracted && (localValue === 0 || localValue === "0")) {
      setLocalValue("");
    }
    setHasInteracted(true);
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setLocalValue(inputValue);
    const newValue = inputValue === "" ? 0 : parseFloat(inputValue) || 0;
    onChange(field, period, item, newValue);
  };

  // Verificar si el campo tiene unidad configurada
  const hasUnit = selectedUnits[field] && selectedUnits[field] !== "";

  // Verificar si las unidades no coinciden
  const unitMismatch =
    item?.[field]?.unit &&
    hasUnit &&
    item[field].unit !== selectedUnits[field];

  if (!editMode) {
    return (
      <Table.Cell textAlign="right">
        {item && item[field] ? (
          <div>
            {formatNumberWithThousands(item[field].value)}
            {unitMismatch && (
              <span
                style={{
                  fontSize: "0.75em",
                  color: "#f57c00",
                  marginLeft: "4px",
                  fontWeight: "bold",
                }}
                title={`Unidad registrada: ${item[field].unit} | Unidad configurada: ${selectedUnits[field]}`}
              >
                ({item[field].unit})
              </span>
            )}
          </div>
        ) : (
          "-"
        )}
      </Table.Cell>
    );
  }

  return (
    <Table.Cell
      textAlign="right"
      style={{
        backgroundColor: isEdited
          ? "#fff9c4"
          : hasUnit
          ? "transparent"
          : "#f5f5f5",
        position: "relative",
        padding: "2px",
      }}
    >
      <input
        type="number"
        step="0.01"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        disabled={!hasUnit}
        style={{
          width: "100%",
          border: isEdited ? "2px solid #fbc02d" : "1px solid #ccc",
          borderRadius: "4px",
          padding: "6px 8px",
          textAlign: "right",
          fontSize: "13px",
          cursor: hasUnit ? "text" : "not-allowed",
          backgroundColor: hasUnit ? "white" : "#f5f5f5",
          color: hasUnit ? "inherit" : "#999",
          MozAppearance: "textfield",
        }}
        placeholder="-"
        title={!hasUnit ? "Debe configurar la unidad primero" : ""}
      />
      {isEdited && (
        <Icon
          name="edit"
          size="small"
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            color: "#fbc02d",
          }}
        />
      )}
      <style>
        {`
          input[type=number]::-webkit-outer-spin-button,
          input[type=number]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
        `}
      </style>
    </Table.Cell>
  );
}
